import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Typography,
  Button,
  Stepper,
  MobileStepper,
  Step,
  StepButton,
  Modal,
  Slider,
  CircularProgress,
  StepLabel,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { createOrUpdateProfile, isProfileEmpty, submitProfilePicture, loadProfilePicture, submitBoatPicture, loadBoatPicture } from '../api/api';
import AvatarEditor from 'react-avatar-editor';
import Check from '@mui/icons-material/Check';
import PropTypes from 'prop-types';
import ProfileEditorBasicInfo from './ProfileEditorBasicInfo';
import ProfileEditorAboutYou from './ProfileEditorAboutYou';
import ProfileEditorAvailability from './ProfileEditorAvailability';
import ProfileEditorAboutYourBoat from './ProfileEditorAboutYourBoat';
import CrewNotificationSetup from './CrewNotificationSetup';

const ProfileEditor = ({ getToken, onProfileLogout, onProfileUpdated, user, profile, domain }) => {
  const { t } = useTranslation();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isPremium = profile?.premiumExp != null && new Date(profile?.premiumExp) > new Date() 
  const [activeStep, setActiveStep] = useState(0);
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    role: 'crew',
    homeClub: null,
    selectedPlaces: [],
    notificationFrequency: 'immediate',
    messageNotifications: true,
    aboutYou: '',
    phoneNumber: '',
    certifications: {},
    activityPreference: {},
    availability: {},
    aboutBoat: '',
    boatName: '',
    showInPublicRoster: false,
    showPhoneInRoster: false,
    showEmailInRoster: false,
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(true);
  const [isAvatarEditorOpen, setIsAvatarEditorOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isSubmittingPicture, setIsSubmittingPicture] = useState(false);
  const [completedSteps, setCompletedSteps] = useState({});
  const [hasPicture, setHasPicture] = useState(false);
  const [boatPicture, setBoatPicture] = useState(null);
  const [boatPicturePreview, setBoatPicturePreview] = useState(null);
  const [isBoatAvatarEditorOpen, setIsBoatAvatarEditorOpen] = useState(false);
  const [isSubmittingBoatPicture, setIsSubmittingBoatPicture] = useState(false);
  const [hasBoatPicture, setHasBoatPicture] = useState(false);
  const boatAvatarEditorRef = useRef(null);
  const [token, setToken] = useState(null);
  const avatarEditorRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    const initToken = async () => {
      const newToken = await getToken();
      setToken(newToken);
    };
    initToken();
  }, [getToken]);

  const steps = [
    { key: 'basicInfo', label: t('profile.editor.steps.basicInfo') },
    { key: 'aboutYou', label: t('profile.editor.steps.aboutYou') },
    { key: (formState.role === 'crew' ? 'availability' : 'aboutBoat'),
      label: formState.role === 'crew' ? t('profile.editor.steps.availability') : t('profile.editor.steps.aboutBoat') },
    { key: 'notifications', label: t('profile.editor.steps.notifications') }
  ];

  // Allow deep-linking to a specific step, e.g. /profile?step=aboutYou
  useEffect(() => {
    const requestedStep = new URLSearchParams(location.search).get('step');
    if (requestedStep) {
      const stepIndex = steps.findIndex(s => s.key === requestedStep);
      if (stepIndex >= 0) {
        setActiveStep(stepIndex);
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (profile) {
      setFormState({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        role: profile.role || 'crew',
        homeClub: profile.homeClub || null,
        selectedPlaces: profile.selectedPlaces || [],
        notificationFrequency: profile.notificationFrequency || 'immediate',
        // Default the message-notification toggle from the match frequency for
        // users who predate this setting: immediate/daily -> on, never -> off.
        messageNotifications: typeof profile.messageNotifications === 'boolean'
          ? profile.messageNotifications
          : (profile.notificationFrequency || 'immediate') !== 'never',
        aboutYou: profile.aboutYou || '',
        phoneNumber: profile.phoneNumber || '',
        certifications: profile.certifications || {},
        activityPreference: profile.activityPreference || {},
        availability: profile.availability || {},
        aboutBoat: profile.aboutBoat || '',
        boatName: profile.boatName || '',
        showInPublicRoster: profile.showInPublicRoster || false,
        showPhoneInRoster: profile.showPhoneInRoster || false,
        showEmailInRoster: profile.showEmailInRoster || false,
      });
      setCompletedSteps(profile.completedSteps || {});
      setHasPicture(profile.hasPicture || false);
      loadUserPictures();
    } else if (user) {
      setFormState(prev => ({
        ...prev,
        firstName: user.given_name || '',
        lastName: user.family_name || '',
      }));
      loadUserPictures();
    }
  }, [user, profile]);

  const loadUserPictures = async () => {
    setIsLoadingPhoto(true);
    try {
      const userId = user?.id || profile?.user_id;

      if (userId) {
        const [profilePictureUrl, boatPictureUrl] = await Promise.all([
          loadProfilePicture(profile),
          loadBoatPicture(profile)
        ]);
        setProfilePicturePreview(profilePictureUrl);
        setHasPicture(!!profilePictureUrl);
        setBoatPicturePreview(boatPictureUrl);
        setHasBoatPicture(!!boatPictureUrl);
      }
    } catch (error) {
      console.error('Error loading pictures:', error);
      setProfilePicturePreview(null);
      setHasPicture(false);
      setBoatPicturePreview(null);
      setHasBoatPicture(false);
    }
    setIsLoadingPhoto(false);
  };

  const updateProfile = async (currentStep) => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Failed to retrieve token');
      }
      const updatedCompletedSteps = {
        ...completedSteps,
        [steps[currentStep].key]: true
      };
      const profileData = { 
        ...formState,
        completedSteps: updatedCompletedSteps,
        hasPicture
      };
      console.log(profile ? "Updating profile.." : "Creating profile..");
      await createOrUpdateProfile(token, profileData);
      setCompletedSteps(updatedCompletedSteps);
      onProfileUpdated();
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const submitProfile = async () => {
    try {
      await updateProfile(activeStep);
      navigate('/');
    } catch (error) {
      console.error('Error submitting profile:', error);
    }
  };

  const handleBoatPictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBoatPicture(file);
      setIsBoatAvatarEditorOpen(true);
    }
  };

  const handleBoatAvatarEditorClose = () => {
    setIsBoatAvatarEditorOpen(false);
    setBoatPicture(null);
  };

  const handleBoatCrop = async () => {
    if (boatAvatarEditorRef.current) {
      setIsSubmittingBoatPicture(true);
      try {
        const canvas = boatAvatarEditorRef.current.getImageScaledToCanvas();
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg'));
        
        const token = await getToken();
        if (!token) {
          throw new Error('Failed to retrieve token');
        }

        await submitBoatPicture(token, blob);
        setBoatPicturePreview(URL.createObjectURL(blob));
        setHasBoatPicture(true);
        await updateProfile(activeStep);
        console.log('Boat picture uploaded successfully');
        onProfileUpdated();
      } catch (error) {
        console.error('Error uploading boat picture:', error);
      } finally {
        setIsSubmittingBoatPicture(false);
        handleBoatAvatarEditorClose();
      }
    }
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePicture(file);
      setIsAvatarEditorOpen(true);
    }
  };

  const handleAvatarEditorClose = () => {
    setIsAvatarEditorOpen(false);
    setProfilePicture(null);
  };

  const handleCrop = async () => {
    if (avatarEditorRef.current) {
      setIsSubmittingPicture(true);
      try {
        const canvas = avatarEditorRef.current.getImageScaledToCanvas();
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg'));
        
        const token = await getToken();
        if (!token) {
          throw new Error('Failed to retrieve token');
        }

        await submitProfilePicture(token, blob);
        setProfilePicturePreview(URL.createObjectURL(blob));
        setHasPicture(true);
        await updateProfile(activeStep); 
        console.log('Profile picture uploaded successfully');
        onProfileUpdated();
      } catch (error) {
        console.error('Error uploading profile picture:', error);
      } finally {
        setIsSubmittingPicture(false);
        handleAvatarEditorClose();
      }
    }
  };

  const handleCancel = () => {
    if (!isProfileEmpty(profile)) {
      navigate('/');
    }
  };

  const handleNext = async () => {
    await updateProfile(activeStep);
    if (activeStep === steps.length - 1) {
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const isFirstStepValid = () => {
    return formState.firstName.trim() !== '' && formState.lastName.trim() !== '' && formState.homeClub !== null;
  };

  const handleWheelZoom = (e) => {
    if (avatarEditorRef.current) {
      const delta = e.deltaY;
      setZoom((prevZoom) => {
        const newZoom = prevZoom - delta / 500;
        return Math.min(Math.max(newZoom, 1), 3);
      });
    }
  };

  const handleFormChange = (field, value) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const allStepsCompleted = steps.every(x=>completedSteps[x.key]);

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ProfileEditorBasicInfo
            domain={domain}
            firstName={formState.firstName}
            setFirstName={(value) => handleFormChange('firstName', value)}
            lastName={formState.lastName}
            setLastName={(value) => handleFormChange('lastName', value)}
            role={formState.role}
            setRole={(value) => handleFormChange('role', value)}
            homeClub={formState.homeClub}
            setHomeClub={(value) => handleFormChange('homeClub', value)}
            token={token}
            onProfileLogout={onProfileLogout}
          />
        );
      case 1:
        return (
          <ProfileEditorAboutYou
            profilePicturePreview={profilePicturePreview}
            isLoadingPhoto={isLoadingPhoto}
            handleProfilePictureChange={handleProfilePictureChange}
            aboutYou={formState.aboutYou}
            setAboutYou={(value) => handleFormChange('aboutYou', value)}
            phoneNumber={formState.phoneNumber}
            setPhoneNumber={(value) => handleFormChange('phoneNumber', value)}
            certifications={formState.certifications}
            handleCertificationChange={(event) => {
              const newCertifications = {
                ...formState.certifications,
                [event.target.name]: event.target.checked,
              };
              handleFormChange('certifications', newCertifications);
            }}
            role={formState.role}
            showInPublicRoster={formState.showInPublicRoster}
            setShowInPublicRoster={(value) => handleFormChange('showInPublicRoster', value)}
            showPhoneInRoster={formState.showPhoneInRoster}
            setShowPhoneInRoster={(value) => handleFormChange('showPhoneInRoster', value)}
            showEmailInRoster={formState.showEmailInRoster}
            setShowEmailInRoster={(value) => handleFormChange('showEmailInRoster', value)}
          />
        );
      case 2:
        return formState.role === 'crew' ? (
          <ProfileEditorAvailability
            activityPreference={formState.activityPreference}
            setActivityPreference={(event) => {
              const newPreference = {
                ...formState.activityPreference,
                [event.target.name]: event.target.checked,
              };
              handleFormChange('activityPreference', newPreference);
            }}
            availability={formState.availability}
            setAvailability={(changes) => {
              const newAvailability = { ...formState.availability };
              changes.forEach(({ day, timeSlot, checked }) => {
                if (!newAvailability[day]) {
                  newAvailability[day] = {};
                }
                newAvailability[day][timeSlot] = checked;
              });
              handleFormChange('availability', newAvailability);
            }}
          />
        ) : (
          <ProfileEditorAboutYourBoat
            boatPicturePreview={boatPicturePreview}
            isLoadingPhoto={isLoadingPhoto}
            handleBoatPictureChange={handleBoatPictureChange}
            aboutBoat={formState.aboutBoat}
            setAboutBoat={(value) => handleFormChange('aboutBoat', value)}
            boatName={formState.boatName}
            setBoatName={(value) => handleFormChange('boatName', value)}
          />
        );
      case 3:
        return (
          <CrewNotificationSetup
            role = {formState.role}
            premium={ isPremium }
            domain={domain}
            token={token}
            homeClub={formState.homeClub}
            selectedPlaces={formState.selectedPlaces}
            setSelectedPlaces={(value) => handleFormChange('selectedPlaces', value)}
            notificationFrequency={formState.notificationFrequency}
            setNotificationFrequency={(value) => handleFormChange('notificationFrequency', value)}
            messageNotifications={formState.messageNotifications}
            setMessageNotifications={(value) => handleFormChange('messageNotifications', value)}
          />
        ) ;
      default:
        return 'Unknown step';
    }
  };

  const QontoStepIconRoot = styled('div')(({ theme }) => ({
    color: '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    '& .QontoStepIcon-completedIcon': {
      color: '#784af4',
      zIndex: 1,
      fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[700],
    }),
    variants: [
      {
        props: ({ ownerState }) => ownerState.active,
        style: {
          color: '#784af4',
        },
      },
    ],
  }));
  
  function QontoStepIcon(props) {
    const { active, completed, className } = props;
  
    return (
      <QontoStepIconRoot ownerState={{ active }} className={className}>
        {completed && !active ? (
          <Check className="QontoStepIcon-completedIcon" />
        ) : (
          <div className="QontoStepIcon-circle" />
        )}
      </QontoStepIconRoot>
    );
  }
  
  QontoStepIcon.propTypes = {
    active: PropTypes.bool,
    className: PropTypes.string,
    completed: PropTypes.bool,
  };

  const mobileSteper = () => {
    return (
      <MobileStepper 
        nonLinear={allStepsCompleted} 
        position="static"
        
        activeStep={activeStep} 
        variant="dots"
        steps={steps.length}
        sx={{ width: '100%', marginTop: "1" }}
        nextButton={
          (activeStep != steps.length - 1) ?
          <Button
            size="medium"
            onClick={handleNext}
            disabled={activeStep === 0 && !isFirstStepValid()}
          >
            Next
            <KeyboardArrowRight /> 
          </Button> : 
             <Button
             sx={{marginLeft: 1}}        
               type="submit"
               color="primary"
               onClick={submitProfile}
             >
               {t('profile.editor.buttons.done')}
             </Button>

        }
        backButton={
          <Button size="medium" onClick={handleBack}  disabled={activeStep === 0}>
             <KeyboardArrowLeft />
            
            Back
          </Button>
        }
      /> );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 800,
          p: 3,
          border: '1px solid #ccc',
          borderRadius: 2,
        }}
      >
        {isMobile && mobileSteper()}
       {isMobile && <Typography variant="h6">{steps[activeStep].label}</Typography>}

       
      {!isMobile && <Stepper nonLinear={allStepsCompleted} activeStep={activeStep} alternativeLabel sx={{ width: '100%', mb: 4 }}>
        {steps.map((step, index) => (
          <Step key={step.key} completed={completedSteps[step.key]}>
            <StepButton onClick={handleStep(index)}>
              <StepLabel StepIconComponent={QontoStepIcon}>{step.label}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper> }
      
        {getStepContent(activeStep)}

        {isMobile &&  allStepsCompleted && <Button
                sx={{marginTop: 1}}       
                disabled={!isFirstStepValid()}
                type="submit"
                variant="outlined"
                color="primary"
                onClick={submitProfile}
              >
                {t('profile.editor.buttons.done')}
              </Button>}
        
     {/* {isMobile && mobileSteper()} */}
      
      {!isMobile &&
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, width: '100%' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack} >
            {t('profile.editor.buttons.back')}
          </Button>  

          <Box>
          <Button
              sx={{margin: 1}}                
              onClick={handleNext}
              variant={allStepsCompleted ? "outlined" : "contained"}
              disabled={activeStep === 0 && !isFirstStepValid()}
            >
              {t('profile.editor.buttons.next')}
            </Button>    

            {(activeStep === steps.length - 1 || allStepsCompleted) && (
              <Button
              disabled={!isFirstStepValid()}
              sx={{marginLeft: 1}}        
                type="submit"
                variant="contained"
                color="primary"
                onClick={submitProfile}
              >
                {t('profile.editor.buttons.done')}
              </Button>
            )}
          </Box>
        </Box>}
      </Box> 
      <Modal
        open={isBoatAvatarEditorOpen}
        onClose={handleBoatAvatarEditorClose}
        aria-labelledby="boat-avatar-editor-modal"
        aria-describedby="modal-to-crop-boat-picture"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {t('profile.editor.modal.cropBoatPicture')}
          </Typography>
          {boatPicture && (
            <Box
              onWheel={(e) => {
                e.preventDefault();
                handleWheelZoom(e);
              }}
              sx={{ cursor: 'zoom-in' }}
            >
              <AvatarEditor
                ref={boatAvatarEditorRef}
                image={URL.createObjectURL(boatPicture)}
                width={250}
                height={250}
                border={50}
                borderRadius={10}
                color={[255, 255, 255, 0.6]}
                scale={zoom}
                rotate={0}
              />
            </Box>
          )}
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography id="boat-zoom-slider" gutterBottom>
              {t('profile.editor.modal.zoom')}
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, newValue) => setZoom(newValue)}
              aria-labelledby="boat-zoom-slider"
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button onClick={handleBoatAvatarEditorClose} disabled={isSubmittingBoatPicture}>
              {t('profile.editor.buttons.cancel')}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleBoatCrop}
              disabled={isSubmittingBoatPicture}
            >
              {isSubmittingBoatPicture ? <CircularProgress size={24} /> : t('profile.editor.buttons.save')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={isAvatarEditorOpen}
        onClose={handleAvatarEditorClose}
        aria-labelledby="avatar-editor-modal"
        aria-describedby="modal-to-crop-profile-picture"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {t('profile.editor.modal.cropProfilePicture')}
          </Typography>
          {profilePicture && (
            <Box
              onWheel={(e) => {
                e.preventDefault();
                handleWheelZoom(e);
              }}
              sx={{ cursor: 'zoom-in' }}
            >
              <AvatarEditor
                ref={avatarEditorRef}
                image={URL.createObjectURL(profilePicture)}
                width={250}
                height={250}
                border={50}
                borderRadius={125}
                color={[255, 255, 255, 0.6]}
                scale={zoom}
                rotate={0}
              />
            </Box>
          )}
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography id="zoom-slider" gutterBottom>
              {t('profile.editor.modal.zoom')}
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, newValue) => setZoom(newValue)}
              aria-labelledby="zoom-slider"
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button onClick={handleAvatarEditorClose} disabled={isSubmittingPicture}>
              {t('profile.editor.buttons.cancel')}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCrop}
              disabled={isSubmittingPicture}
            >
              {isSubmittingPicture ? <CircularProgress size={24} /> : t('profile.editor.buttons.save')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProfileEditor;
